import escapeHtml from "escape-html";
import GraphemeSplitter from "grapheme-splitter";
import { autoLink } from "../components/url/links";

/**
 * テキスト処理ユーティリティ（旧 `TextProcessMixin` の composable 版）。
 *
 * 文字数カウント・検索メタ文字エスケープ・URL リンク化＋検索ハイライト・
 * カタカナ→ひらがな変換を提供する。`v-html` 経由で挿入される文字列を扱うため、
 * サニタイズ挙動（`escapeHtml` / `autoLink`）は旧 mixin と同一に保つこと。
 *
 * 1-D（mixin → composable）。利用側の書き換えは 1-C 高リスク群（PR-3）で実施する。
 */
export function useTextProcess() {
  const splitter = new GraphemeSplitter();

  // 文字数カウント
  const getLength = (str: string): number => {
    return splitter.splitGraphemes(str).length;
  };

  // 検索文字の前処理(メタ文字のエスケープ)
  const processed = (search: string): string => {
    return escapeHtml(search).replace(/[.^$|\\[\](){}+*?]/g, "\\$&");
  };

  // 対象文字の検索
  const searchWord = (target: string, search: string): boolean => {
    const regExp = new RegExp(processed(search), "i");
    return regExp.test(escapeHtml(target));
  };

  // URLリンク化 + 検索結果該当箇所ハイライト処理
  const highLightStr = (target: string, search: string | undefined): string => {
    // URLハイパーリンク化
    const message: string = autoLink(target);
    if (!search) {
      return message;
    }
    const processedSearch = processed(search);
    if (!processedSearch || !processedSearch.length) {
      return message;
    }

    const tmpMatches = message.match(/<a href=.*?>|<\/a>/gi);
    const tmpArray = message
      .replace(/<a href=.*?>|<\/a>/gi, "/**/")
      .split(/\/\*\*\//g);
    const regExp = new RegExp(processedSearch, "ig");

    // ハイライト用のclass挿入
    const highlight = tmpArray.map((tmp) => {
      return tmp.replace(regExp, "<span class='highlight'>$&</span>");
    });

    let result = "";
    if (tmpMatches == null) {
      result = highlight[0];
    } else {
      for (let i = 0; i < tmpMatches.length; i++) {
        result += highlight[i] + tmpMatches[i];
      }
      result += highlight[tmpMatches.length];
    }
    return result;
  };

  // カタカナ→ひらがな変換
  const katakanaToHiragana = (str: string): string => {
    return str.replace(/[ァ-ヺ]/g, (ch) => {
      return String.fromCharCode(ch.charCodeAt(0) - 0x60);
    });
  };

  return {
    getLength,
    processed,
    searchWord,
    highLightStr,
    katakanaToHiragana,
  };
}
